import React, { FunctionComponent, ReactNode } from 'react';
import { useClasses, IStyles } from 'app/utils/css';
import DropdownOverlay, { DROPDOWN_LEFT_ALIGNED, DROPDOWN_RIGHT_ALIGNED } from 'app/components/layout/DropdownOverlay';
import ActionArrow from 'app/components/svgs/ActionArrow';
import { NavButton } from 'app/components/presentation/nav/NavLink';
import useToggle from '@engoo/react-hooks/useToggle';
import { useOnFocusOutside } from 'app/utils/react';
import { ExtractProps } from 'app/utils/types';

interface IProps {
  alignRight?: boolean;
  hideOnClick?: boolean;
  showArrow?: boolean;
  title?: ReactNode;
  icon?: ReactNode;
}

type NavDropdownProps = IProps & ExtractProps<typeof DropdownOverlay>;

const NavDropdown: FunctionComponent<NavDropdownProps> = ({
  alignRight = false,
  hideOnClick = true,
  renderHidden = true,
  showArrow = true,
  title,
  icon,
  children,
  ...rest
}) => {
  const [isDropdownOpen, toggleDropdownOpen] = useToggle(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const hideDropdown = () => {
    if (isDropdownOpen) {
      toggleDropdownOpen();
    }
  };

  const onOverlayClick = () => {
    if (hideOnClick) {
      toggleDropdownOpen();
    }
  };

  const classes = useClasses({
    container: {
      position: 'relative',
      display: 'flex',
      alignSelf: 'stretch',
    },
    title: {
      marginRight: icon ? '8px' : undefined,
    },
  });

  const overlayStyles: IStyles = {
    display: 'flex',
    flexDirection: 'column',
  };

  useOnFocusOutside({ getNode: () => containerRef.current, callback: hideDropdown });

  return (
    <span ref={containerRef} className={classes.container}>
      <NavButton onClick={toggleDropdownOpen}>
        {title && <div className={classes.title}>{title}</div>}
        {icon}
        {showArrow && <ActionArrow size="8px" style={{ marginLeft: '8px' }} />}
      </NavButton>
      <DropdownOverlay
        {...rest}
        onClick={onOverlayClick}
        styles={overlayStyles}
        isOpen={isDropdownOpen}
        renderHidden={renderHidden}
        variant={alignRight ? DROPDOWN_RIGHT_ALIGNED : DROPDOWN_LEFT_ALIGNED}
      >
        {children}
      </DropdownOverlay>
    </span>
  );
};

export default NavDropdown;
